import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DateOfBirthPicker from '../components/DateOfBirthPicker';
import { registerMember, getMembershipPackages } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
];

const initialForm = {
  fullName: '', idNumber: '', dateOfBirth: '', gender: '', phoneNumber: '',
  email: '', residentialAddress: '', province: '', occupation: '', employer: '',
  membershipPackageId: '', referralCode: '', password: '', confirmPassword: '',
};

export default function Register() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ ...initialForm, referralCode: searchParams.get('ref') || '' });
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    getMembershipPackages().then((res) => setPackages(res.data)).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirmPassword;
      if (payload.membershipPackageId) payload.membershipPackageId = Number(payload.membershipPackageId);

      const res = await registerMember(payload);
      login(res.data);
      setSuccessInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (successInfo) {
    return (
      <div>
        <PageHeader eyebrow="Registration Complete" title="Welcome to the Organisation" />
        <section className="section">
          <div className="container" style={{ maxWidth: 520 }}>
            <div className="card">
              <div className="success-box">Your account has been created successfully.</div>
              <p><strong>Username:</strong> {successInfo.username}</p>
              <p><strong>Membership Number:</strong> {successInfo.membershipNumber}</p>
              <p style={{ fontSize: '0.85rem' }}>
                Please save your username somewhere safe — you'll use it (or your email) to log in.
              </p>
              <button className="btn btn-gold btn-block" onClick={() => navigate('/member/dashboard')}>
                Go to My Dashboard
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Become a Member"
        title="Membership Registration"
        subtitle="Join Singumkhonto Wabasebenzi and get access to your personal member portal."
      />
      <section className="section">
        <div className="container" style={{ maxWidth: 720 }}>
          <div className="card">
            {error && <p className="error-text" style={{ marginBottom: 16 }}>{error}</p>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input name="fullName" required value={form.fullName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>ID Number</label>
                  <input name="idNumber" required value={form.idNumber} onChange={handleChange} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date of Birth</label>
                  <DateOfBirthPicker
                    value={form.dateOfBirth}
                    onChange={(iso) => setForm({ ...form, dateOfBirth: iso })}
                  />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select name="gender" value={form.gender} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input name="phoneNumber" required value={form.phoneNumber} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input type="email" name="email" required value={form.email} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label>Residential Address</label>
                <input name="residentialAddress" value={form.residentialAddress} onChange={handleChange} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Province</label>
                  <select name="province" required value={form.province} onChange={handleChange}>
                    <option value="">Select your province</option>
                    {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Occupation</label>
                  <input name="occupation" value={form.occupation} onChange={handleChange} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Employer</label>
                  <input name="employer" value={form.employer} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Membership Package</label>
                  <select name="membershipPackageId" value={form.membershipPackageId} onChange={handleChange}>
                    <option value="">Select a package</option>
                    {packages.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} (R{p.monthlyFee})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Referral Code (optional)</label>
                <input name="referralCode" value={form.referralCode} onChange={handleChange} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Password</label>
                  <input type="password" name="password" required value={form.password} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Confirm Password</label>
                  <input type="password" name="confirmPassword" required value={form.confirmPassword} onChange={handleChange} />
                </div>
              </div>

              <button type="submit" className="btn btn-gold btn-block" disabled={loading}>
                {loading ? 'Creating your account...' : 'Register'}
              </button>

              <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.9rem' }}>
                Already a member? <Link to="/login" style={{ color: 'var(--color-gold)', fontWeight: 600 }}>Log in</Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
